import React, { Component, ComponentType, ReactNode } from 'react'
import { Box, colors, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemProps, ListItemText, styled } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { ClosePopover, GlobalModalState, mapGlobalModalContext, PopoverGlobal } from '../api-context'

export type ITableGridItemActionType = 'Edit' | 'Delete'

const mapIcons: { [key in ITableGridItemActionType]: ReactNode } = {
  Edit: <EditIcon fontSize='small' color='primary' />,
  Delete: <DeleteOutlineIcon fontSize='small' color='error' />
}

export interface ITableGridItemActionsBaseProps<T> {
  value: T
}

export type IActionFormGetter<T> = (value: T) => React.ReactNode

export interface IActionMenuItemDef<T> {
  name: string
  icon?: ReactNode
  /** Whether this item should trigger a close action when clicked. @default true */
  closableOnClick?: boolean
  /** Handler called when item is clicked */
  onClick?: (event: React.MouseEvent<HTMLLIElement, MouseEvent>, value: T) => void
  listItemProps?: ListItemProps
  listItemPropsGetter?: (value: T) => ListItemProps & { [key: string]: any }
  /**
   * Optional custom ReactNode to override the entire ListItem UI.
   * If provided, this will be rendered instead of the default ListItem structure
   * (icon + text + click behavior).
   */
  override?: ComponentType<{ onClose?: ClosePopover }>
}

export interface TableGridItemActionsConfig<T> {
  formEdit?: IActionFormGetter<T>
  formDelete?: IActionFormGetter<T>
  buttonEditConfig?: GlobalModalState
  buttonDeleteConfig?: GlobalModalState
  menuList?: {
    before?: SingleOrArray<IActionMenuItemDef<T>>
    after?: SingleOrArray<IActionMenuItemDef<T>>
  }
}

export interface ITableGridItemActionsProps<T> extends ITableGridItemActionsBaseProps<T>, TableGridItemActionsConfig<T> {}

export function CreateTableGridItemActions<T>() {
  class TableGridItemActions extends Component<ITableGridItemActionsProps<T>> {
    render() {
      if (!this.isVisible()) return <></>
      return (
        <Wrap>
          <IconButton
            size='small'
            onClick={(e) => {
              PopoverGlobal.Api.open({
                anchorEl: e.currentTarget,
                popoverProps: { anchorOrigin: { vertical: 'top', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' } },
                content: (
                  <List>
                    {this.renderItems(this.props.menuList?.before)}
                    {this.renderItemWithForm('Edit', this.props.formEdit, this.props.buttonEditConfig)}
                    {this.renderItemWithForm('Delete', this.props.formDelete, this.props.buttonEditConfig ?? { backdropActivated: true })}
                    {this.renderItems(this.props.menuList?.after)}
                  </List>
                )
              })
            }}
          >
            <MoreVertIcon fontSize='small' />
          </IconButton>
        </Wrap>
      )
    }

    isVisible = () => {
      const { menuList } = this.props
      const list = [...this.singleOrArrayToArray(menuList?.before), ...this.singleOrArrayToArray(menuList?.after)]
      return !!this.props.formEdit || !!this.props.formDelete || list.length > 0
    }

    singleOrArrayToArray = (value?: SingleOrArray<IActionMenuItemDef<T>>): IActionMenuItemDef<T>[] => {
      if (!value) return []
      return Array.isArray(value) ? value : [value]
    }

    renderItems = (value?: SingleOrArray<IActionMenuItemDef<T>>) => {
      if (!value) return <></>
      const list = Array.isArray(value) ? value : [value]
      return list.map((item, index) => {
        const key = item.name + index
        const { override: Override } = item
        if (Override) return <Override key={key} onClose={PopoverGlobal.Api.close} />
        const itemProps = item.listItemPropsGetter ? item.listItemPropsGetter(this.props.value) : item.listItemProps
        return (
          <ListItemCustom
            key={key}
            disablePadding
            onClick={(e) => {
              if (item.closableOnClick !== false) PopoverGlobal.Api.close()
              item.onClick && item.onClick(e, this.props.value)
            }}
            {...itemProps}
          >
            <ListItemButton>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.name} />
            </ListItemButton>
          </ListItemCustom>
        )
      })
    }

    renderItemWithForm = (name: ITableGridItemActionType, form?: IActionFormGetter<T>, globalState?: GlobalModalState) => {
      if (!form) return <></>
      return mapGlobalModalContext((context) => (
        <ListItemCustom
          disablePadding
          onClick={() => {
            PopoverGlobal.Api.close()
            context.show({ renderContent: () => form(this.props.value), ...globalState })
          }}
        >
          <ListItemButton>
            <ListItemIcon>{mapIcons[name]}</ListItemIcon>
            <ListItemText primary={name} />
          </ListItemButton>
        </ListItemCustom>
      ))
    }
  }
  return TableGridItemActions
}

const Wrap = styled(Box)({
  background: colors.common.white,
  borderRadius: '6px',
  '& > .MuiButtonBase-root': {
    borderRadius: '6px'
  }
})

const ListItemCustom = styled(ListItem)({
  minWidth: '150px',
  '.MuiListItemIcon-root': {
    minWidth: '40px'
  },
  '.MuiListItemText-root .MuiTypography-root': {
    color: colors.grey[800]
  },
  '&:hover': {
    '.MuiListItemText-root .MuiTypography-root': {
      background: colors.grey[100],
      color: colors.grey[900]
    }
  }
})
